import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { setSelectedAssignedFolderId } from "@store/slices/devnagriValidatorAdminSlice";
import { restrictSpecialCharacters, ROLE_ROUTES } from "@utils/constants";
import { fetchInProgressFolders, fetchInProgressFiles } from "@store/thunks/devnagriValidationAdminThunk";

const FolderDetailTableDevnagriValidatorAdmin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const { selectedAssignedProjectId, assignedFolders = {} } = useSelector((store) => store.devnagriValidatorAdmin);

  const [searchQuery, setSearchQuery] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });
  const [hasNextPage, setHasNextPage] = useState(true); 

  // Handle input change and key press
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const handleKeyDown = (e) => {
    setSearchQuery(e.target.value);
  };

  // Fetch folders when project is selected
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        if (selectedAssignedProjectId) {
          setInitialLoading(true);
          const res = await dispatch(fetchInProgressFolders(selectedAssignedProjectId));
          if (Array.isArray(res)) {
            if (res.length < pageSize) {
              setHasNextPage(false);
            } else {
              setHasNextPage(true);
            }
            setInitialLoading(false);
          }
          setInitialLoading(false);
        } else {
          setInitialLoading(false);
        }
      } catch (error) {
        console.error("Error fetching folders:", error);
        setError("Failed to fetch folders: " + error.message);
        setInitialLoading(false);
      }
    };

    fetchFolders();
  }, [dispatch, selectedAssignedProjectId]);

  const handleFolderClick = async ({ row }) => {
    try {
      setLoading(true);
      console.log("Row data on folder click:", row);
      const folderId = row.original.folderId;
      if (!folderId) {
        setLoading(false);
        return;
      }
      dispatch(setSelectedAssignedFolderId(folderId));
      // await dispatch(fetchInProgressFiles(folderId));
      navigate(`${ROLE_ROUTES["DEVANAGARI ADMIN"]}/folders/files`);
    } catch (error) {
      console.error("Error handling folder click:", error);
      setError("Failed to fetch files: " + error.message);
    } finally {
      setLoading(false);
    }
  };


  const folderColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "folderName",
      header: () => "Folder Name",
      accessorKey: "folderName",
      cell: ({ row }) => (
        <button
          onClick={() => handleFolderClick({ row })}
          className="text-[#ec7026] hover:text-[#d85d1a] focus:underline"
        >
          {row.original.folderName}
        </button>
      ),
    },
    {
      header: 'File Count',
      accessorKey: "fileCount",
      cell: ({ row }) => {
        const fileCount = row.original?.fileCount;
        return (
          <div className="text-center">
            {fileCount !== undefined ? fileCount : "N/A"}
          </div>
        );
      },
      cellClassName: "text-center",
    },
    {
      id: "createdAt",
      header: () => "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => {
        const uploadDate = row.original?.createdAt || row.createdAt;
        if (!uploadDate) return "N/A";
        try {
          const date = new Date(uploadDate);
          return (
            <div>
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return uploadDate;
        }
      },
      cellClassName: "text-center",
    },
  ];

  const currentFolders = Array.isArray(assignedFolders[selectedAssignedProjectId])
    ? assignedFolders[selectedAssignedProjectId]
    : [];


  // Filter folders based on search query
  const filteredFolders = useMemo(() => {
    if (!searchQuery) return currentFolders;
    return currentFolders.filter(
      (project) =>
        project.folderName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [currentFolders, searchQuery]);

  // Loading state
  if (initialLoading || loading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>{error}</p>
            <p className="mt-2">Please try again later or contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  // No project selected state
  if (!selectedAssignedProjectId) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-gray-500 text-sm font-['Semantic/Strong']">
            Please select a project to view folders.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Folders..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="w-full overflow-x-auto">
          {filteredFolders.length === 0 ? (
            <div className="p-6">
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                <p>No folders found.</p>
                <p className="mt-2">
                  You have not been assigned any folders. Please contact the administrator if this seems incorrect.
                </p>
              </div>
            </div>
          ) : (
            <GenericTable
              data={[...filteredFolders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
              columns={folderColumns}
              showPagination={true}
              manualPagination={true}
              pageIndex={pageIndex}   
              pageSize={pageSize} 
              hasNextPage={hasNextPage}
              onPaginationChange={({ pageIndex, pageSize }) => {
                setPageIndex(pageIndex);
                setPageSize(pageSize);
              }}
              emptyMessage="No folders available for this project"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderDetailTableDevnagriValidatorAdmin;